// FRAC / HRAC / IRAC rotation card — surfaces resistance-management risk
// per zone. Flagged groups (red ring) are ones with ≥2 applications of the
// same mode-of-action code inside 30 days.

import { useEffect, useState } from 'react';
import type { FracRotationGroup } from '@greeniq/shared';
import { getFracRotation } from '../api/chemical.js';

interface Props {
  courseSlug: string;
  zoneId: number;
}

export function FracRotationCard({ courseSlug, zoneId }: Props) {
  const [groups, setGroups] = useState<FracRotationGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getFracRotation(courseSlug, zoneId, 180);
        if (!cancelled) setGroups(r.groups);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug, zoneId]);

  if (error) {
    return (
      <section className="mt-8 p-5 bg-bg-card rounded-lg border border-border text-sm text-[color:var(--color-error)]">
        FRAC rotation unavailable: {error}
      </section>
    );
  }

  return (
    <section className="mt-8 p-5 bg-bg-card rounded-lg border border-border">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm uppercase tracking-wider text-fg-muted font-semibold">
          FRAC / HRAC / IRAC rotation · last 180 days
        </h2>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-bg-hover text-greeniq-300 uppercase tracking-wider">
          Resistance management
        </span>
      </div>

      {groups === null ? (
        <p className="mt-3 text-sm text-fg-muted">Loading…</p>
      ) : groups.length === 0 ? (
        <p className="mt-3 text-sm text-fg-muted">
          No applications logged for this zone in the last 180 days. Log a spray to start the rotation history.
        </p>
      ) : (
        <>
          <ul className="mt-4 divide-y divide-border">
            {groups.map((g) => (
              <li key={`${g.system}-${g.moaCode}`} className="py-3 flex items-start gap-3 flex-wrap">
                <span
                  className={[
                    'px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider',
                    g.recentResistanceRisk
                      ? 'bg-error-muted text-[color:var(--color-error)] ring-2 ring-red-300'
                      : 'bg-bg-hover text-greeniq-300',
                  ].join(' ')}
                  title={g.recentResistanceRisk ? '≥2 applications of this MOA within 30 days' : ''}
                >
                  {g.system} {g.moaCode}
                </span>
                <div className="flex-1 min-w-[200px]">
                  <div className="text-sm text-fg">
                    {g.applicationCount} application{g.applicationCount === 1 ? '' : 's'}
                    <span className="ml-2 text-xs text-fg-muted">· last {fmtShort(g.mostRecent)}</span>
                  </div>
                  <div className="text-xs text-fg-muted mt-0.5 truncate">
                    {g.products.join(', ')}
                  </div>
                </div>
                {g.recentResistanceRisk && (
                  <span className="text-xs text-[color:var(--color-error)] font-semibold">
                    Rotate before next spray
                  </span>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-fg-muted">
            Red-ringed groups had ≥2 applications within the last 30 days. Rotate to a different FRAC / HRAC / IRAC code to reduce resistance pressure.
          </p>
        </>
      )}
    </section>
  );
}

function fmtShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
